import axios from "axios";
import { createApi } from "@reduxjs/toolkit/query/react";
import { authEndpoints } from "./endpoints/authEndpoints";
import { dataEndpoints } from "./endpoints/dataEndpoints";

const axiosInstance = axios.create();

const getToken = (key) =>
  localStorage.getItem(key) || sessionStorage.getItem(key);

const setToken = (key, value) => {
  if (localStorage.getItem(key)) {
    localStorage.setItem(key, value);
  } else if (sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, value);
  }
};

const removeToken = (key) => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

const axiosBaseQuery =
  ({ baseUrl }) =>
  async ({ url, method, data, params }) => {
    let accessToken = getToken("accessToken");
    const refreshToken = getToken("refreshToken");

    try {
      const result = await axiosInstance({
        url: baseUrl + url,
        method,
        data,
        params,
        headers: {
          Authorization: `Bearer ${accessToken || refreshToken}`,
        },
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError;

      if (err.response?.status === 401) {
        // Nếu có refreshToken và token hết hạn
        if (refreshToken) {
          try {
            const refreshResponse = await axiosInstance.post(
              `${baseUrl}/api/v1/consumers/auth/refresh`,
              { refresh_token: refreshToken }
            );
            accessToken = refreshResponse.data.access_token;
            setToken("accessToken", accessToken); // lưu vào chỗ token đang tồn tại

            // Retry request với token mới
            const retryResult = await axiosInstance({
              url: baseUrl + url,
              method,
              data,
              params,
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            return { data: retryResult.data };
          } catch (refreshError) {
            // Refresh cũng fail
            removeToken("accessToken");
            removeToken("refreshToken");
            window.location.href = "/sign-in";
            return {
              error: {
                status: 401,
                data: "Unauthorized - Redirecting to sign-in",
              },
            };
          }
        } else {
          // Không có refreshToken
          removeToken("accessToken");
          removeToken("refreshToken");
          window.location.href = "/sign-in";
          return {
            error: {
              status: 401,
              data: "Unauthorized - Redirecting to sign-in",
            },
          };
        }
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

// Validate base URL
const rawBaseUrl = process.env.REACT_APP_BASE_URL;

// Fix environment variable caching issue
const baseUrl = rawBaseUrl === 'http://localhost:3001/api' 
  ? 'https://api.letsrally.dev' 
  : rawBaseUrl;

console.log('🔍 API Configuration Check:');
console.log('- Raw Base URL:', rawBaseUrl || 'undefined');
console.log('- Corrected Base URL:', baseUrl || 'undefined');

if (!baseUrl) {
  console.error('❌ REACT_APP_BASE_URL is missing! API calls will fail.');
} else {
  console.log('✅ API Base URL configured');
}

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: axiosBaseQuery({
    baseUrl: baseUrl || 'http://localhost:3001', // fallback for development
  }),
  endpoints: (builder) => ({
    ...authEndpoints(builder),
    ...dataEndpoints(builder),
  }),
});

export const {
  // auth
  useLoginMutation,
  useLogoutMutation,
  useSignupMutation,
  useVerifyOTPMutation,
  useVerifyOTPSignUpMutation,
  useResendOTPSignUpMutation,
  // Profile
  useGetUserInfoQuery,
  useUpdateUserInfoMutation,
  usePostFileUploadMutation,
  usePostUpdateAvatarMutation,
  useLazyGetInboxListQuery,
  useGetUnreadInboxCountQuery,
  usePostReadInboxMutation,
  // Quest
  useLazyGetQuestListQuery,
  useLazyGetQuestListPerLocationMainQuery,
  useLazyGetQuestListPerLocationQuery,
  useLazyGetOngoingQuestListPerLocationQuery,
  useLazyGetFavoriteLocationsQuery,
  useLazyGetQuestHistoryListQuery,
  useGetTakeQuestQuery,
  usePostTakeQuestPracticeMutation,
  usePostAnswerMutation,
  usePostQuestReviewMutation,
  useGetQuestDetailsQuery,
  usePostEndQuestMutation,
  usePostSaveQuestMutation,
  usePostStartQuestMutation,
  useGetLocationByLatLongQuery,
  useLazyGetTimeExtensionQuery,
  useGetQuestDetailsPublicQuery,
  usePostPauseTimerParticipantMutation,
  // useLazyGetQuestDetailsQuery,
  useLazyGetTakeQuestPracticeQuery,
  useLazyGetDetailParticipantQuery,
  usePostFileTakeQuestMutation,
  usePostTakeQuestCompleteMutation,
  useFavoritesQuestMutation,
  //tutorials
  useGetTutorialsQuery,
  useGetTutorialDetailsQuery,
  usePostTutorialStatusMutation,
  usePostNextTutorialMutation,
  //withdraw
  usePostWithdrawMutation,
  useGetWithdrawHistoryQuery,
  useGetWithdrawDetailsQuery,
  useGetCalculateTokenQuery,
  //Contact
  usePostContactMutation,

  // Location
  // useLazyGetQuestListPerLocationMainQuery,
  usePostFavoriteLocationMutation,
} = apiSlice;
